import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader2, LogIn } from 'lucide-react';
import { setAccessTokenGetter } from '../services/api';
import { getBaseOriginUrl } from '../utils/basePath';
import { clearSageSession } from '../utils/sageSession';

export default function AuthGuard({ children }) {
  const { isAuthenticated, isLoading, error, loginWithRedirect, getAccessTokenSilently, logout } = useAuth0();

  // Register the token getter so axios can attach JWT to requests
  useEffect(() => {
    if (isAuthenticated) {
      console.log('[AuthGuard] Setting access token getter');
      setAccessTokenGetter(() =>
        getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          },
        })
      );
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Listen for 401 events and trigger re-login
  useEffect(() => {
    const handleUnauthorized = () => {
      clearSageSession();
      logout({ logoutParams: { returnTo: getBaseOriginUrl() } });
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [logout]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img src="/favicon.svg" alt="Sunny Benefits" className="w-16 h-16" />
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full mx-4 text-center">
          <img src="/favicon.svg" alt="Sunny Benefits" className="w-14 h-14 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Authentication Error</h2>
          <p className="text-sm text-gray-500 mb-6">{error.message}</p>
          <button
            onClick={() => loginWithRedirect()}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full mx-4 text-center">
          <img src="/favicon.svg" alt="Sunny Benefits" className="w-14 h-14 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-1">LENS</h1>
          <p className="text-sm text-gray-500 mb-6">Leveraging Engineering Numbers & Statistics</p>
          <button
            onClick={() => loginWithRedirect()}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return children;
}
